import { Badge, LayerCard, LinkButton, Text } from '@cloudflare/kumo'
import { UserPlusIcon } from '@phosphor-icons/react'
import { useTranslation } from '../../i18n'
import { formatDuration } from '../../lib/format'
import type { NextUp } from './agenda'
import { GuestRow } from './GuestRow'
import { onPlainClick } from './plain-click'

export interface NextUpCardProps {
  nextUp: NextUp
  /** Open the Invite sheet for this booking instead of navigating. */
  onInviteGuest?: (booking: NextUp['booking']) => void
}

/** The ongoing (or next today/tomorrow) booking with its guests and the two
 *  actions people need right before a meeting. */
export function NextUpCard({ nextUp, onInviteGuest }: NextUpCardProps) {
  const { t } = useTranslation()
  const { booking, state, minutes, startHm, endHm, guests } = nextUp
  const duration = formatDuration(minutes, t)
  const label =
    state === 'ongoing'
      ? t('dashboard.nextUp.happeningNow', { duration })
      : state === 'today'
        ? t('dashboard.nextUp.startsIn', { duration })
        : t('dashboard.nextUp.tomorrow')

  return (
    <section aria-labelledby="home-next-up">
      <LayerCard>
        <LayerCard.Primary>
          <div className="flex flex-col gap-3">
            <div>
              <Badge variant={state === 'ongoing' ? 'success' : 'info'}>{label}</Badge>
            </div>
            <div>
              <Text as="h2" variant="heading2" id="home-next-up">
                {booking.roomName ?? t('dashboard.nextUp.room')}
              </Text>
              <p className="text-sm text-kumo-subtle tabular-nums">
                {startHm} – {endHm} · {t('dashboard.nextUp.yourBooking')}
              </p>
            </div>
            {guests.length > 0 ? (
              <div>
                <p className="text-sm font-medium">
                  {t(
                    guests.length === 1
                      ? 'dashboard.nextUp.guestsInvitedOne'
                      : 'dashboard.nextUp.guestsInvited',
                    { count: guests.length },
                  )}
                </p>
                <ul className="home-list">
                  {guests.map((inv) => (
                    <GuestRow key={inv.id} invitation={inv} variant="nested" />
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-kumo-subtle">{t('dashboard.nextUp.noGuests')}</p>
            )}
            <div className="home-actions-row">
              <LinkButton
                href={`/my/guests/new?roomBookingId=${encodeURIComponent(booking.id)}`}
                onClick={onPlainClick(onInviteGuest && (() => onInviteGuest(booking)))}
                variant="primary"
                size="lg"
                icon={<UserPlusIcon size={18} />}
              >
                {t('dashboard.nextUp.inviteGuest')}
              </LinkButton>
              <LinkButton
                href={`/my/bookings/${encodeURIComponent(booking.id)}/edit`}
                variant="secondary"
                size="lg"
              >
                {t('dashboard.nextUp.details')}
              </LinkButton>
            </div>
          </div>
        </LayerCard.Primary>
      </LayerCard>
    </section>
  )
}
